import { datadogRum } from "@datadog/browser-rum";
import { trackAnalytic } from "./trackEvent";
import { getIsInIframe } from "components/main/teleop/util/iframeHelpers";
import { useEffect, useRef } from "react";
import { useDatadogContext } from "components/main/teleop/DatadogContextProvider";

/** Event name used for all TTL analytics */
export const METRICS_EVENT_NAME = "streaming.ttl";

/** Tracks which tags have already been reported to avoid duplicates */
const encounteredTags = new Set<string>();

/**
 * Tracks a tag only on its first occurrence.
 * Subsequent calls with the same tag are ignored.
 *
 * We send to both trackAnalytic and datadogRum.addTiming because they provide
 * two different ways to analyze data in Datadog:
 * - trackAnalytic: Custom events queryable in RUM Explorer
 * - datadogRum.addTiming: Performance timings visible in Session Replay and Performance Monitoring
 *
 * @param tag - Unique identifier for the timing checkpoint
 * @param time - Timestamp in ms
 */
function trackFirstEncounter(tag: string, time: number, tabVisible: boolean) {
  if (encounteredTags.has(tag)) {
    return;
  }
  encounteredTags.add(tag);
  trackAnalytic(METRICS_EVENT_NAME, {
    tag,
    time,
    origin: window.location.origin,
    tabVisible,
    inIframe: getIsInIframe(),
  });
  datadogRum.addTiming(`${METRICS_EVENT_NAME}.${tag}`, time);
}

interface TTLMoment {
  tag: string;
  time: number;
  tabVisible: boolean;
}

/**
 * Hooks into the DatadogContextProvider to track TTLs.
 * If a tag is passed, it will track it right away.
 */
export function useTrackTTL(tag?: string) {
  const ttlMoments = useRef<TTLMoment[] | null>([]);

  //  Note: We specifically ensure that trackTTL is stable, to avoid any re-triggering of
  //  the useEffect hook that calls it. That's why they are defined with useRef and not useCallback.
  const trackTTLRef = useRef((tag: string, t?: number) => {
    const time = t ?? performance.now();
    const tabVisible = !document.hidden;
    const savedTTLMoments = ttlMoments.current;
    const datadogReady = savedTTLMoments === null;
    if (datadogReady) {
      trackFirstEncounter(tag, time, tabVisible);
    } else {
      savedTTLMoments.push({ tag, time, tabVisible });
    }
  });

  //  Flush all saved tracked events when user.id and orgUuid are ready, and set ttlMoments queue
  //  to null to make sure we stop using it
  const { hasUser, hasOrgUuid } = useDatadogContext();
  useEffect(() => {
    if (!hasUser && !hasOrgUuid) return;
    //  Flush all ttlMoments
    ttlMoments.current?.forEach(moment => {
      trackFirstEncounter(moment.tag, moment.time, moment.tabVisible);
    });
    ttlMoments.current = null;
  }, [hasUser, hasOrgUuid]);

  //  For convenience, if we passed a tag, we just track it right away
  useEffect(() => {
    if (tag) {
      trackTTLRef.current(tag);
    }
  }, [tag]);

  return trackTTLRef.current;
}

/**
 * Track navigation TTLs.
 */
export function useTrackNavigationTTL() {
  const trackTTL = useTrackTTL();
  useEffect(() => {
    /**
     * Tracks:
     * - http_request_start: When the browser started the HTTP request
     * - time_to_first_byte: When the first response byte was received
     * - html_download_complete: When the HTML document finished downloading
     * - page_fully_loaded: When all resources (including iframes) finished loading
     */
    const navigationEntry: PerformanceNavigationTiming | undefined = performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming | undefined;
    if (!navigationEntry) {
      console.warn("No navigation entry found");
      return;
    }

    const httpRequestStart = navigationEntry.requestStart - navigationEntry.startTime;
    const timeToFirstByte = navigationEntry.responseStart - navigationEntry.startTime;
    const htmlDownloadComplete = navigationEntry.responseEnd - navigationEntry.startTime;
    const pageFullyLoaded = navigationEntry.loadEventEnd - navigationEntry.startTime;

    trackTTL("http_request_start", httpRequestStart);
    trackTTL("time_to_first_byte", timeToFirstByte);
    trackTTL("html_download_complete", htmlDownloadComplete);
    trackTTL("page_fully_loaded", pageFullyLoaded);
  }, [trackTTL]); //  One time execution, no dependencies except trackTTL, which is stable
}

/** @internal Clears tracked tags. Exported for testing purposes only. */
export function _resetForTesting() {
  encounteredTags.clear();
}
